import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Popover } from "antd";
import "./conversation.title.box.icons.scss";
import {
  ArchiveIcon,
  CallAddIcon,
  EditIcon,
  MailIcon,
  MergeIcon,
  MoreHorizontalIcon,
  SmsIcon,
  TagIcon,
} from "@Assets/icons";
import {
  ISharedFullConversation,
  ConversationStatusOptions,
  ConversationPriorityOptions,
  IPreviewQueue,
  Nullable,
  hasVoiceLicense,
  DraftChannelEnum,
  canEmail,
  IPreviewConversation,
  ISharedPatchConversation,
  ISharedConversation,
  ConversationStatusEnum,
  ChannelEnum,
} from "atlas-shared";

import { useTranslation } from "react-i18next";
import { IWithFullConversationProps } from "@Hocs/with.full.conversation";
import { FormSelect } from "atlas-form";
import { useClients, useQueues } from "@Hooks";
import { AlertError, conversationMessageReply, entitiesToOptions, IOption } from "@Utils";
import {
  patchConversation as _patchConversation,
  endChatSession,
  getChatSessionsByConversationId,
} from "@Api";
import { actionAiResponding, IAuth, useAppDispatch } from "@Store";
import {
  VoiceSuggestions,
  Icon,
  ConversationDispositionPopover,
  ConversationMergePopover,
  ConversationTagPopover,
  IconText,
} from "@Components";
import { FeatureFlag } from "@Containers/FeatureFlag";
import { useSearchParams, useParams } from "react-router-dom";

const createdDrafts: Record<string, boolean> = {};

interface IProps {
  auth: IAuth;
  conversation: ISharedFullConversation;
  className?: string;
  patch_conversation: IWithFullConversationProps["patch_conversation"];
  short?: boolean;
}
export const ConversationTitleBoxIcons = React.memo(
  ({ conversation, className, patch_conversation, auth, short = false }: IProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [hasCreatedDraft, setHasCreatedDraft] = useState(false);
    const [queueOptions, setQueueOptions] = useState<Array<IOption>>([]);
    const [clientOptions, setClientOptions] = useState<Array<IOption>>([]);
    const queues = useQueues();
    const clients = useClients();
    const [queue, setQueue] = useState<Nullable<IPreviewQueue>>(null);
    const { id: urlConversationId } = useParams();

    // Message type is set when the user clicks on the "New Text Message" button in the
    // minimized conversation view (tab.header.tsx.)
    const messageType = searchParams.get("message_type");
    const shouldCreateDraft =
      !hasCreatedDraft &&
      messageType === "text_message" &&
      urlConversationId &&
      String(conversation.id) === String(urlConversationId);

    useEffect(() => {
      setQueue(
        queues.loaded && conversation.queue_id ? queues.dict[conversation.queue_id] || null : null
      );
    }, [queues, conversation.queue_id]);

    useEffect(() => {
      setQueueOptions(entitiesToOptions(queues.queues).map((o) => ({ ...o, key: o.key })));
    }, [queues]);

    useEffect(() => {
      setClientOptions(
        queue?.clients?.length
          ? entitiesToOptions(
              clients.clients.filter((client) => queue.clients.includes(client.id))
            ).map((o) => ({ ...o, key: o.key }))
          : []
      );
    }, [clients, queue]);

    const conversationMerge = useMemo(
      () => (
        <ConversationMergePopover
          patch_conversation={patch_conversation}
          conversation={conversation}
        >
          {!short ? (
            <Icon tooltip={{ title: t("MERGE_CONVERSATION") }} icon={MergeIcon} />
          ) : (
            <IconText icon={MergeIcon} text={t("MERGE_CONVERSATION")} />
          )}
        </ConversationMergePopover>
      ),
      [short]
    );

    const createInternalNote = useMemo(
      () =>
        !short ? (
          <Icon
            icon={EditIcon}
            tooltip={{ title: t("CREATE_INTERNAL_NOTE") }}
            onClick={(_) =>
              conversationMessageReply(
                t,
                auth.user,
                DraftChannelEnum.InternalNote,
                conversation,
                queue
              )
            }
          />
        ) : (
          <IconText
            icon={EditIcon}
            text={t("CREATE_INTERNAL_NOTE")}
            onClick={(_) =>
              conversationMessageReply(
                t,
                auth.user,
                DraftChannelEnum.InternalNote,
                conversation,
                queue
              )
            }
          />
        ),
      [short, auth, conversation, queue]
    );

    const createMail = useMemo(
      () =>
        conversation.queue_id && !short ? (
          <Icon
            icon={MailIcon}
            tooltip={{ title: t("NEW_MAIL_MESSAGE") }}
            onClick={(_) =>
              conversationMessageReply(t, auth.user, DraftChannelEnum.Mail, conversation, queue)
            }
          />
        ) : (
          <IconText
            icon={MailIcon}
            text={t("NEW_MAIL_MESSAGE")}
            onClick={(_) =>
              conversationMessageReply(t, auth.user, DraftChannelEnum.Mail, conversation, queue)
            }
          />
        ),
      [short, auth, queue, conversation]
    );

    const createMailWithAiRequest = useCallback(() => {
      dispatch(actionAiResponding(true));
      conversationMessageReply(
        t,
        auth.user,
        DraftChannelEnum.Mail,
        conversation,
        queue,
        undefined,
        false,
        false,
        true
      ).finally(() => {
        dispatch(actionAiResponding(true));
      });
    }, [auth.user, conversation, queue]);

    const createMailWithAi = useMemo(
      () =>
        queue?.ai_assistant_id && !short ? (
          <Icon
            icon={MailIcon}
            tooltip={{ title: t("NEW_MAIL_MESSAGE_WITH_AI") }}
            onClick={(_) => createMailWithAiRequest()}
          />
        ) : (
          <IconText
            icon={MailIcon}
            text={t("NEW_MAIL_MESSAGE_WITH_AI")}
            onClick={(_) => createMailWithAiRequest()}
          />
        ),
      [short, auth, queue, conversation]
    );

    const createTextMessage = useMemo(
      () =>
        conversation.queue_id && !short ? (
          <Icon
            icon={SmsIcon}
            tooltip={{ title: t("NEW_TEXT_MESSAGE") }}
            onClick={() =>
              conversationMessageReply(t, auth.user, DraftChannelEnum.Sms, conversation, queue)
            }
          />
        ) : (
          <IconText
            icon={SmsIcon}
            text={t("NEW_TEXT_MESSAGE")}
            onClick={() =>
              conversationMessageReply(t, auth.user, DraftChannelEnum.Sms, conversation, queue)
            }
          />
        ),
      [short, auth, queue, conversation]
    );

    const addCall = useMemo(
      () =>
        auth.user.main &&
        hasVoiceLicense(auth.user) && (
          <Popover
            trigger="click"
            placement="bottom"
            content={
              <>
                <VoiceSuggestions conversation={conversation} />
              </>
            }
          >
            {!short ? (
              <Icon icon={CallAddIcon} tooltip={{ title: t("ADD_CALL_TO_CONVERSATION") }} />
            ) : (
              <IconText icon={CallAddIcon} text={t("ADD_CALL_TO_CONVERSATION")} />
            )}
          </Popover>
        ),
      [auth, short, conversation]
    );

    const patchConversation = useCallback(
      (
        organization_id: IPreviewConversation["organization_id"],
        id: ISharedConversation["id"],
        payload: ISharedPatchConversation
      ) => {
        _patchConversation(organization_id, id, payload).catch((e) =>
          AlertError(t, { content: t(e.toString()) })
        );
      },
      []
    );

    const closeChatSession = useCallback(async (conversationId: number) => {
      const chatSessions = await getChatSessionsByConversationId(conversationId);
      if (chatSessions[0]?.sessionId) {
        endChatSession(chatSessions[0].sessionId);
      }
    }, []);

    useEffect(() => {
      // We use a key to prevent double creation of drafts when clicking the "New Text Message" button
      // while the conversation is already open in the minimized view.
      const draftKey = `${conversation.id}-text_message`;
      if (
        messageType === "text_message" &&
        urlConversationId &&
        String(conversation.id) === String(urlConversationId) &&
        !createdDrafts[draftKey]
      ) {
        createdDrafts[draftKey] = true; // Set immediately to prevent double creation
        conversationMessageReply(t, auth.user, DraftChannelEnum.Sms, conversation, queue).then(
          () => {
            searchParams.delete("message_type");
            setSearchParams(searchParams);
          }
        );
      }
    }, [
      messageType,
      urlConversationId,
      conversation.id,
      t,
      auth.user,
      queue,
      searchParams,
      setSearchParams,
    ]);

    return (
      <div className={"conversation-title-view-icons " + className}>
        <div className="left">
          <FormSelect
            value={conversation.status}
            options={ConversationStatusOptions(t)}
            onChange={async (status) => {
              const shouldCloseChatSession =
                conversation.main_channel === ChannelEnum.Chat &&
                status === ConversationStatusEnum.Closed;

              if (shouldCloseChatSession) {
                closeChatSession(conversation.id);
              }
              patch_conversation({ status });
            }}
            showSearch={false}
          />
          <FormSelect
            value={conversation.priority}
            options={ConversationPriorityOptions(t)}
            onChange={(priority) =>
              patchConversation(conversation.organization_id, conversation.id, { priority })
            }
            showSearch={false}
          />
          <FormSelect
            value={conversation.queue_id}
            options={queueOptions}
            onChange={(queue_id) =>
              patchConversation(conversation.organization_id, conversation.id, { queue_id })
            }
            placeholder={t("SELECT_QUEUE")}
          />
          {!!clientOptions?.length && (
            <FormSelect
              value={conversation.client_id}
              options={clientOptions}
              onChange={(client_id) =>
                patchConversation(conversation.organization_id, conversation.id, { client_id })
              }
              placeholder={t("SELECT_CLIENT")}
            />
          )}
        </div>
        <div className="right">
          {queue && (
            <ConversationDispositionPopover
              patch_conversation={patch_conversation}
              conversation={conversation}
            >
              <Icon tooltip={{ title: t("DISPOSE") }} icon={ArchiveIcon} />
            </ConversationDispositionPopover>
          )}
          {!short && conversationMerge}
          {queue && (
            <ConversationTagPopover
              queue={queue}
              patch_conversation={patch_conversation}
              conversation={conversation}
            >
              <Icon tooltip={{ title: t("EDIT_TAGS") }} icon={TagIcon} />
            </ConversationTagPopover>
          )}
          {!short && addCall}
          {!short && (
            <FeatureFlag identifier="text.message" defaultValue={false}>
              {createTextMessage}
            </FeatureFlag>
          )}
          {!short && createInternalNote}
          {!short && createMail}
          {short && (
            <Popover
              trigger="click"
              placement="bottom"
              overlayClassName={"conversation-more-actions-popover-container"}
              content={
                <ul>
                  <li>{conversationMerge}</li>
                  <li>{createInternalNote}</li>
                  {addCall && <li>{addCall}</li>}
                  {canEmail(auth.user) && <li>{createMail}</li>}
                  {canEmail(auth.user) && <li>{createMailWithAi}</li>}
                  <FeatureFlag identifier="text.message" defaultValue={false}>
                    <li>{createTextMessage}</li>
                  </FeatureFlag>
                </ul>
              }
            >
              <Icon icon={MoreHorizontalIcon} className={"clickable"} />
            </Popover>
          )}
        </div>
      </div>
    );
  }
);
